/*
  Roots hero stage
  ================

  Two jobs, both of which have to be right before the first paint or the home page
  visibly jumps:

  1. THE CHROME CONTRACT. On the home page the hero sits *under* the chrome,
     which stays in normal flow. The hero is pulled up by exactly the chrome's
     height, so it needs that number as a CSS variable. It is measured rather
     than hard-coded because it is merchant data: the logo asset, the logo
     height setting and whether the localization selectors are on all move it.
     The announcement bar is measured too and is normally absent — the section
     was removed from the header group, but a merchant can add it back from the
     theme editor, and then it is part of the same number. The
     `--roots-chrome-h` fallback in the stylesheet covers the frames before
     this runs.

  2. THE SEQUENCE. Cross-dissolve between slides, each one drifting slightly while
     it is visible. DESIGN.md allows the hero exactly one animated moment; this is
     that moment, extended across several photographs rather than added alongside
     the old one.

  THE SEQUENCE DOES NOT RUN ITSELF. It advances when a reader asks it to, through
  the tick rail. That is a deliberate removal, not a missing feature: auto-advance
  obliges a visible pause control under WCAG 2.2.2, and that control was the one
  glyph-in-a-roundel in a system built out of hairlines. Rather than keep a
  foreign shape to license a timer nobody asked for, both went.

  The cross-dissolve is untouched — DESIGN.md commits this system to one moving
  idiom, and it still has it. What changed is who starts it.

  `prefers-reduced-motion: reduce` still drops the dissolve to a plain swap
  through the stylesheet; there is no timer left for it to stop here.
*/

class RootsHeroStage extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.ticks = Array.from(this.querySelectorAll('[data-hero-tick]'));
    this.liveRegion = this.querySelector('[data-hero-live]');
    this.index = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains('is-active'))
    );

    this.measureChrome();

    if (this.slides.length > 1) this.bindControls();
  }

  disconnectedCallback() {
    if (this.chromeObserver) this.chromeObserver.disconnect();
    if (this.solidObserver) this.solidObserver.disconnect();
  }

  /* --- 1. Chrome ---------------------------------------------------------- */

  measureChrome() {
    if (!this.hasAttribute('data-overlay-header')) return;

    const announce = document.querySelector('.announcement-bar-section');
    const header = document.querySelector('.section-header');
    if (!header) return;

    const apply = () => {
      // `offsetHeight` over `getBoundingClientRect` on purpose: once the header
      // is stuck its rect is still its own box, but a transform on an ancestor
      // (Shopify's theme editor uses one) would scale the rect and not the
      // layout height the negative margin has to cancel.
      const announceHeight = announce ? announce.offsetHeight : 0;
      const chromeHeight = announceHeight + header.offsetHeight;
      const root = document.documentElement;
      root.style.setProperty('--roots-announce-h', `${announceHeight}px`);
      root.style.setProperty('--roots-chrome-h', `${chromeHeight}px`);
    };

    apply();
    document.documentElement.classList.add('roots-chrome-overlay');

    // The lift is applied to the Shopify section wrapper so it moves the page
    // itself, rather than to the hero, where it would only work as long as
    // margin-collapsing happens to reach <main>.
    const wrapper = this.closest('.shopify-section');
    if (wrapper) wrapper.classList.add('roots-section--overlaid');

    if ('ResizeObserver' in window) {
      this.chromeObserver = new ResizeObserver(apply);
      if (announce) this.chromeObserver.observe(announce);
      this.chromeObserver.observe(header);
    } else {
      window.addEventListener('resize', apply);
    }

    this.watchForSolidChrome();
  }

  /*
    The chrome goes solid the moment the header is actually stuck to the top of
    the viewport — not at some invented scroll distance. A one-pixel sentinel
    sits at the bottom edge of the announcement bar, so the browser tells us when
    that line crosses the top of the screen and no scroll handler runs at all.
  */
  watchForSolidChrome() {
    const sentinel = this.querySelector('[data-chrome-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;

    this.solidObserver = new IntersectionObserver(
      ([entry]) => {
        document.documentElement.classList.toggle('roots-chrome-solid', !entry.isIntersecting);
      },
      { threshold: 0 }
    );
    this.solidObserver.observe(sentinel);
  }

  /* --- 2. Sequence -------------------------------------------------------- */

  bindControls() {
    this.ticks.forEach((tick, i) => {
      tick.addEventListener('click', () => this.show(i));
    });
  }

  show(next) {
    const count = this.slides.length;
    const target = ((next % count) + count) % count;
    if (target === this.index) return;

    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'true');
    this.slides[target].classList.add('is-active');
    this.slides[target].removeAttribute('aria-hidden');

    this.ticks.forEach((tick, i) => {
      tick.setAttribute('aria-current', i === target ? 'true' : 'false');
    });

    if (this.liveRegion) {
      this.liveRegion.textContent = `${target + 1} / ${count}`;
    }

    this.index = target;
  }
}

if (!customElements.get('roots-hero-stage')) {
  customElements.define('roots-hero-stage', RootsHeroStage);
}
